import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import SettingRow from "../common/SettingRow";

export default function StorefrontDeleteButton({ storefrontId, label, onDeleted, onError, onSaved }: {
  storefrontId: number;
  label: string;
  onDeleted: () => void;
  onError: (message: string) => void;
  onSaved?: (message: string) => void;
}) {
  const { del } = useApi();

  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteStorefront = async () => {
    setDeleting(true);
    try {
      await del(`/api/storefronts/${storefrontId}`);
      setConfirming(false);
      onSaved?.("Storefront deleted");
      onDeleted();
    } catch (err: any) {
      onError(err?.message || "Failed to delete the storefront");
      setConfirming(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <SettingRow
        label="Delete storefront"
        description="Only for a row added by mistake. A Shopify store's storefront cannot be deleted."
        control={
          <Button color="error" variant="text" size="small" onClick={() => setConfirming(true)}>
            Delete storefront
          </Button>
        }
      />
      <Dialog
        open={confirming}
        onClose={(_, reason) => { if (reason === "backdropClick") return; setConfirming(false); }}
      >
        <DialogTitle>Delete storefront {label}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Its logo, policies and support email go with it. Disputes on this domain will print
            without them until it is added again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteStorefront}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
